import {
  updateUpgradePlan,
  useGetUpgradePlan,
  useListUpgradePlanStages,
} from 'api/frontend';
import { Breadcrumbs, SkeletonText } from 'subframe/index';
import { ConsoleLoader } from 'components/Loader';
import MessageBox from 'components/MessageBox';
import { Stack } from 'components/utils';
import Page from 'components/Page';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { useNavigate, useParams } from 'react-router';
import { isExampleData, UPGRADE_STAGES, UPGRADES } from 'src/data/upgrades';
import { UpgradesViewComponent } from '../UpgradesView';
import styles from '../UpgradesView.module.scss';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { useSWRConfig } from 'swr';
import { useTemplateIdToNameMap } from '../helpers/useTemplateIdToNameMap';
import { RouterLink } from 'components/RouterLink';
import { Actions, AtomicTooltip } from 'components/design-system';
import classNames from 'classnames';
import {
  showCancel,
  UpgradeViewMode,
  getTooltipForUpgradeCompletedBy,
  showArchive,
} from '../helpers/UpgradesHelpers';
import { useState } from 'react';
import { UpgradePlan } from 'api/models';
import MarkUpgradePlanAsCompleted from '../components/MarkUpgradePlanAsCompletedDialog';
import useAnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import * as Sentry from '@sentry/browser';
import OwnerSelector from '../components/OwnerSelector';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import UpgradesCancelDialog from '../helpers/CancelEntryDialog';
import { useExampleData } from 'hooks/useExampleData';
import MarkUpgradeAsArchived from '../components/MarkUpgradeAsArchived';

export default function UpgradesClusterView() {
  const { upgradeId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = useAnalyticsEventLogger();

  const { account } = useUserAccountState();
  const basePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/plans/clusters',
  );
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();

  // Check if the currently open Upgrade Plan is an example or real
  const exampleData = upgradeId !== undefined && isExampleData(upgradeId);
  const [showExampleData, setShowExampleData] = useExampleData();
  // update view in localStorage if it doesn't match the currently open Upgrade Plan
  if (exampleData !== showExampleData) {
    setShowExampleData(exampleData);
  }

  const {
    data: upgradePlanData,
    error: upgradePlanError,
    mutate: mutateUpgradePlan,
  } = useGetUpgradePlan(upgradeId || '', {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
    swr: {
      enabled: !exampleData,
    },
  });
  const { data: upgradePlanStagesData, error: upgradePlanStagesError } =
    useListUpgradePlanStages(
      upgradeId || '',
      { page_size: 100 },
      {
        request: { headers: { Authorization: `Bearer ${account.token}` } },
        swr: {
          enabled: !exampleData,
        },
      },
    );

  //redirect to the upgrade clusters page if there is an error in displaying stage data
  if (upgradePlanData && upgradePlanStagesError) {
    navigate(basePath);
    if (
      upgradePlanStagesError?.response?.status === 404 &&
      upgradePlanData?.status === 'cancelled'
    ) {
      enqueueSnackbar('No data available for the canceled Upgrade Plan', {
        variant: 'warning',
        autoHideDuration: toastAutoHideDuration,
      });
    } else if (
      upgradePlanStagesError?.response?.status === 400 &&
      upgradePlanData?.status === 'pending'
    ) {
      enqueueSnackbar(
        'An Upgrade Plan is being generated for you. You will receive a notification when the Plan is ready',
        {
          variant: 'warning',
          autoHideDuration: toastAutoHideDuration,
        },
      );
    } else {
      enqueueSnackbar('No data available for the Upgrade Plan', {
        variant: 'error',
        autoHideDuration: toastAutoHideDuration,
      });
    }
  }

  const upgradePlan = exampleData
    ? UPGRADES.find((u) => u.id === upgradeId)
    : upgradePlanData;
  const upgradePlanStages = exampleData
    ? UPGRADE_STAGES.get(upgradeId || '')
    : upgradePlanStagesData?.data;

  const clusterResourcesCount = (upgradePlan?.resources?.clusters || []).length;
  if (!clusterResourcesCount) {
    const updatedPath = window.location.pathname.replace('clusters', 'addons');
    navigate(updatedPath);
  }

  const templateIdToNameMap = useTemplateIdToNameMap({
    token: account.token || '',
    useExampleData: showExampleData,
  });

  const upgradeTemplatesBasePath = useAccountIdRoute(
    `/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters`,
  );

  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [openMarkCompletedDialog, setOpenMarkCompletedDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openMarkUpgradeAsArchivedDialog, setOpenMarkUpgradeAsArchivedDialog] =
    useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<UpgradePlan>();

  if (upgradePlan === undefined || upgradePlanStages === undefined) {
    return (
      <Page title={'Upgrade Details'}>
        <div
          className={styles['pageContents']}
          style={{ marginLeft: '32px', width: 'calc(100% - 64px)' }}
        >
          <Breadcrumbs>
            <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item>Upgrade Plans</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <SkeletonText className="w-[200px]" />
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item active={true}>
              {upgradePlan?.name || upgradeId}
            </Breadcrumbs.Item>
          </Breadcrumbs>
          {upgradePlanError || upgradePlanStagesError ? (
            <Stack className="w-full h-full justify-center">
              <MessageBox
                mode={'error'}
                message={
                  upgradePlanError?.response?.status === 404
                    ? 'Upgrade Plan not found'
                    : 'An error occurred while fetching the Upgrade Plan'
                }
              />
            </Stack>
          ) : (
            <ConsoleLoader />
          )}
        </div>
      </Page>
    );
  } else {
    const actions = (
      <>
        <Actions
          size="large"
          sections={[
            {
              title: 'Update Properties',
              items: [
                <Actions.Item
                  key="edit-name"
                  title="Edit Name"
                  icon="FeatherEdit2"
                  onClick={() => {
                    setSelectedUpgrade(upgradePlan);
                    setOpenEditNameDialog(true);
                  }}
                  disabled={showExampleData}
                  tooltip={
                    showExampleData ? 'Example plans cannot be updated' : ''
                  }
                />,

                <OwnerSelector
                  key="change-owner"
                  onConfirm={async (newOwner) => {
                    if (showExampleData) {
                      enqueueSnackbar('Example plans cannot be updated', {
                        variant: 'warning',
                        autoHideDuration: toastAutoHideDuration,
                      });
                    } else {
                      try {
                        await updateUpgradePlan(
                          upgradePlan.id,
                          { owner: { id: newOwner } },
                          {
                            headers: {
                              Authorization: `Bearer ${account.token}`,
                            },
                          },
                        );
                        logEvent('upgrades-clusters-plan-owner-change', {
                          planId: upgradePlan.id,
                          ownerId: newOwner,
                        });
                        mutateUpgradePlan();
                        enqueueSnackbar(
                          'Successfully updated the owner of the Upgrade Plan',
                          {
                            variant: 'success',
                            autoHideDuration: toastAutoHideDuration,
                          },
                        );
                      } catch (err) {
                        enqueueSnackbar(
                          somethingWentWrong.replace(
                            '<action>',
                            'updating the owner of the Upgrade Plan',
                          ),
                          {
                            variant: 'error',
                            autoHideDuration: toastAutoHideDuration,
                          },
                        );
                        Sentry.captureException(err);
                      }
                    }
                  }}
                />,
              ],
            },
            {
              title: 'Lifecycle Actions',
              items: [
                <Actions.Item
                  key="mark-completed"
                  title="Mark as Completed"
                  icon="FeatherCheck"
                  onClick={() => {
                    setSelectedUpgrade(upgradePlan);
                    setOpenMarkCompletedDialog(true);
                  }}
                  disabled={
                    showExampleData || upgradePlan.status !== 'in-progress'
                  }
                  tooltip={
                    showExampleData
                      ? 'Example plans cannot be updated'
                      : upgradePlan.status !== 'in-progress'
                      ? 'Action is only available for In Progress Upgrade Plans'
                      : ''
                  }
                />,
                <Actions.Item
                  key="cancel-plan"
                  title="Cancel Plan"
                  icon="FeatherX"
                  onClick={() => {
                    setSelectedUpgrade(upgradePlan);
                    setOpenCancelDialog(true);
                  }}
                  disabled={showExampleData || !showCancel(upgradePlan)}
                  tooltip={
                    showExampleData
                      ? 'Example plans cannot be canceled'
                      : !showCancel(upgradePlan)
                      ? 'Action is not available for this Upgrade Plan'
                      : ''
                  }
                />,
                <Actions.Item
                  key="mark-archived"
                  title="Archive"
                  icon="FeatherArchive"
                  onClick={() => {
                    setOpenMarkUpgradeAsArchivedDialog(true);
                  }}
                  disabled={showExampleData || !showArchive(upgradePlan)}
                  tooltip={
                    showExampleData
                      ? 'Action is not available on example data'
                      : !showArchive(upgradePlan)
                      ? 'Action is not available for this Upgrade Plan'
                      : ''
                  }
                />,
              ],
            },
          ]}
        />
        <ChangeNameDialog
          key="edit-name"
          open={openEditNameDialog}
          setOpen={setOpenEditNameDialog}
          mode={UpgradeViewMode.Plan}
          name={selectedUpgrade?.name || ''}
          onConfirm={async (newName) => {
            if (showExampleData) {
              enqueueSnackbar('Example plans cannot be updated', {
                variant: 'warning',
                autoHideDuration: toastAutoHideDuration,
              });
            } else {
              try {
                await updateUpgradePlan(
                  selectedUpgrade?.id || '',
                  { name: newName },
                  {
                    headers: {
                      Authorization: `Bearer ${account.token}`,
                    },
                  },
                );
                logEvent('upgrades-clusters-plan-name-change', {
                  planId: selectedUpgrade?.id || '',
                  name: newName,
                });
                mutateUpgradePlan();
                enqueueSnackbar(
                  'Successfully edited the requested properties of the Upgrade Plan',
                  {
                    variant: 'success',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
              } catch (err) {
                enqueueSnackbar(
                  somethingWentWrong.replace(
                    '<action>',
                    'updating requested properties of the Upgrade Plan',
                  ),
                  {
                    variant: 'error',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
                Sentry.captureException(err);
              }
            }
          }}
        />
        <UpgradesCancelDialog
          open={openCancelDialog}
          setOpen={setOpenCancelDialog}
          mode={UpgradeViewMode.Plan}
          onConfirm={async () => {
            if (showExampleData) {
              enqueueSnackbar('Example plans cannot be canceled', {
                variant: 'warning',
                autoHideDuration: toastAutoHideDuration,
              });
            } else {
              try {
                await updateUpgradePlan(
                  selectedUpgrade?.id || '',
                  { status: 'cancelled' },
                  {
                    headers: {
                      Authorization: `Bearer ${account.token}`,
                    },
                  },
                );
                logEvent('upgrades-clusters-plan-cancel', {
                  planId: selectedUpgrade?.id || '',
                  name: selectedUpgrade?.name || '',
                });
                mutateUpgradePlan();
                enqueueSnackbar('Successfully canceled Upgrade Plan', {
                  variant: 'success',
                  autoHideDuration: toastAutoHideDuration,
                });
              } catch (err) {
                enqueueSnackbar(
                  somethingWentWrong.replace(
                    '<action>',
                    'canceling this Upgrade Plan',
                  ),
                  {
                    variant: 'error',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
                Sentry.captureException(err);
              }
            }
          }}
        />
        <MarkUpgradePlanAsCompleted
          upgradeId={selectedUpgrade?.id || ''}
          mutatePlan={() => mutateUpgradePlan()}
          open={openMarkCompletedDialog}
          onClose={() => setOpenMarkCompletedDialog(false)}
        />
        <MarkUpgradeAsArchived
          upgradeId={selectedUpgrade?.id || ''}
          mutateUpgrade={() => {}}
          open={openMarkUpgradeAsArchivedDialog}
          onClose={() => setOpenMarkUpgradeAsArchivedDialog(false)}
        />
      </>
    );
    return (
      <UpgradesViewComponent
        actions={actions}
        mode={UpgradeViewMode.Plan}
        account={account}
        upgradePlan={upgradePlan}
        stages={upgradePlanStages}
        navigate={navigate}
        basePath={basePath}
        breadcrumb={'Clusters'}
        stagesStepsDefaultOpen={false}
        onChange={(mutateStage = true, mutatePlan = false) => {
          mutatePlan && mutateUpgradePlan();
          mutateStage &&
            globalMutate((key) => {
              if (Array.isArray(key)) {
                if (
                  (key[0] as string).startsWith(`/upgrades/${upgradePlan.id}/`)
                ) {
                  return true;
                }
              }
              return false;
            });
        }}
        additionalHeaderElements={
          <>
            {upgradePlan.upgrade_template_id && (
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>Upgrade Template:</span>

                <RouterLink
                  className="hoverable-link text-body"
                  to={`${upgradeTemplatesBasePath}/${upgradePlan.upgrade_template_id}`}
                >
                  <AtomicTooltip tooltipContent="Click to view template">
                    <span>
                      {templateIdToNameMap.get(
                        upgradePlan.upgrade_template_id,
                      ) || upgradePlan.upgrade_template_id}
                    </span>
                  </AtomicTooltip>
                </RouterLink>
              </div>
            )}
            {upgradePlan.owner && (
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>Owner:</span>

                <span className={classNames(styles['text'], 'ph-no-capture')}>
                  {upgradePlan.owner?.name || upgradePlan.owner?.id || ''}
                </span>
              </div>
            )}
            {upgradePlan.completed_by && upgradePlan.updated && (
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>{`Marked Completed by:`}</span>
                <AtomicTooltip
                  tooltipContent={getTooltipForUpgradeCompletedBy(
                    upgradePlan.completed_by?.name,
                    upgradePlan.updated,
                  )}
                  className="ph-no-capture"
                >
                  <span className={classNames(styles['text'], 'ph-no-capture')}>
                    {upgradePlan.completed_by?.name || ''}
                  </span>
                </AtomicTooltip>
              </div>
            )}
          </>
        }
      />
    );
  }
}
